export const getPhoneNumberDigits = (
  value: string | null | undefined,
): string => (value ?? "").replace(/\D/g, "");

/**
 * Display form of a 10/11-digit US number: "(415) 555-0100" / "+1 (415) 555-0100".
 * Anything else is returned unchanged.
 */
export const formatUsPhoneNumber = (phoneNumber: string) => {
  const cleaned = `${phoneNumber}`.replace(/\D/g, "");
  let match;
  if (cleaned.length === 11) {
    match = cleaned.match(/^(\d)(\d{3})(\d{3})(\d{4})$/);
    if (match) {
      return `+${match[1]} (${match[2]}) ${match[3]}-${match[4]}`;
    }
  } else if (cleaned.length === 10) {
    match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);
    if (match) {
      return `(${match[1]}) ${match[2]}-${match[3]}`;
    }
  }
  return phoneNumber;
};

const MAX_EXTENSION_LENGTH = 10;

/**
 * Comparable form of an extension: trimmed and length-capped. Kept verbatim
 * otherwise (an extension can be a short code, and leading zeros matter).
 */
export const normalizeExtension = (value: string | null | undefined): string =>
  (value ?? "").trim().slice(0, MAX_EXTENSION_LENGTH);

/**
 * The row in `candidates` carrying the same number as `value`, or null.
 *
 * Reuse-before-create for `StoredPhoneNumber`, mirroring
 * `findFaxNumberByDigits`. Identity here is the dialed number **and** the
 * extension: a shared switchboard reached at two different extensions is two
 * legitimate rows, not a duplicate — same rule the add/edit modal enforces.
 */
export const findPhoneNumberByDigitsAndExtension = <
  T extends {
    id?: string | null;
    phoneNumber?: string | null;
    extension?: string | null;
  },
>(
  candidates: readonly T[],
  value: string | null | undefined,
  extension: string | null | undefined,
): T | null => {
  const digits = getPhoneNumberDigits(value);
  if (!digits) return null;
  const ext = normalizeExtension(extension);
  return (
    candidates.find(
      (candidate) =>
        getPhoneNumberDigits(candidate.phoneNumber) === digits &&
        normalizeExtension(candidate.extension) === ext,
    ) ?? null
  );
};

/**
 * Progressive formatter for live input: the parentheses / spacing appear as
 * digits are typed, e.g. "4" → "(4", "4155" → "(415) 5", "4155550100" →
 * "(415) 555-0100". Only the closing "-"/spacing is added lazily so the value
 * stays deletable (no mask re-insertion trap). Used by the phone input field.
 */
export const formatPhoneNumber = (value: string | null | undefined): string => {
  const digits = getPhoneNumberDigits(value).slice(0, 10);

  if (digits.length === 0) return "";
  if (digits.length < 4) return `(${digits}`;
  if (digits.length < 7) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
};

/**
 * Display formatter for dropdown labels and the legacy "Original" reference:
 * a complete 10/11-digit number is rendered as "(415) 555-0100"
 * (or "+1 (415) 555-0100"); anything else is returned verbatim.
 */
export const formatPhoneNumberForDisplay = (
  value: string | null | undefined,
): string => {
  const originalValue = value?.trim() ?? "";
  if (!originalValue) return "";

  return formatUsPhoneNumber(originalValue);
};

/**
 * Renders a phone number together with its optional extension, e.g.
 * "(415) 555-0100 ext. 22". Unlike the number, the extension is kept verbatim
 * (it can be a short code) and is only appended when present. Used by the
 * dropdown labels and the legacy "Original" reference display.
 */
export const formatPhoneNumberWithExtension = (
  value: string | null | undefined,
  extension: string | null | undefined,
): string => {
  const formatted = formatPhoneNumberForDisplay(value);
  const ext = extension?.trim() ?? "";

  if (!formatted) return "";
  return ext ? `${formatted} ext. ${ext}` : formatted;
};
